import type { UseQueryOptions } from '@tanstack/react-query'

import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  PoktanClassDataType,
  PoktanClassInfiniteQueryOptions,
  PoktanClassResponseDataType
} from './types'

const POKTAN_CLASS_QUERY_KEY = (params: {
  page: number
  per_page: number
  province_code?: string
  city_code?: string
  district_code?: string
  village_code?: string
  search: string
}) => ['reference_poktan_class', { ...params }] as const

type PoktanClassQueryKey = ReturnType<typeof POKTAN_CLASS_QUERY_KEY>

const getBatch = (
  opts?: Partial<{
    page: number
    per_page: number
    search: string
  }>
): PoktanClassInfiniteQueryOptions<PoktanClassQueryKey> => {
  const params = {
    page: opts?.page ?? 1,
    per_page: opts?.per_page ?? 10,
    search: opts?.search ?? ''
  }

  return {
    queryKey: POKTAN_CLASS_QUERY_KEY(params),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const meta = lastPage.meta

      if (!meta) return undefined
      return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
        ? (meta?.current_page ?? 0) + 1
        : undefined
    },
    queryFn: async () => {
      try {
        const res = await api.get<PoktanClassResponseDataType>(
          '/report/reference/kelas-poktan',
          {
            params: {
              search: params.search.length >= 2 ? params.search : undefined
            }
          }
        )

        return {
          message: res.data.message,
          data: res.data.data,
          meta: res.data.meta
        }
      } catch (ex) {
        throw handleAxiosError(ex)
      }
    }
  }
}

const POKTAN_CLASS_BY_ID_QUERY_KEY = (params: { id: string }) =>
  ['reference_poktan_class', params] as const

const getById = (opts: {
  id: string
}): UseQueryOptions<ServiceGetByIdResponse<PoktanClassDataType>> => ({
  queryKey: POKTAN_CLASS_BY_ID_QUERY_KEY(opts),
  queryFn: async () => {
    try {
      const res = await api.get<ServiceGetAllResponse<PoktanClassDataType[]>>(
        '/report/reference/kelas-poktan',
        {
          params: {
            id: opts.id
          }
        }
      )

      const data = res.data.data?.[0]
      if (!data) {
        throw new Error('Poktan class not found')
      }

      return {
        message: res.data.message,
        data
      }
    } catch (ex) {
      throw handleAxiosError(ex)
    }
  }
})

export const poktanClassReference = {
  queryKey: {
    all: POKTAN_CLASS_QUERY_KEY,
    byId: POKTAN_CLASS_BY_ID_QUERY_KEY
  },
  batched: getBatch,
  byId: getById
}
